// The outbox drain: how this device's changes reach the list. Every change
// is made here first and waits in the store's outbox (IndexedDB) until the
// list answers it, so nothing made offline, or in a tab that closed, is lost.
//
// The rules, per socket:
// - Nothing goes until the list has answered the socket's hello (a snapshot
//   or the changes missed) and that answer is applied here: then every
//   pending change goes, oldest first, the order they were made.
// - At most DRAIN_WINDOW changes are in flight (sent, not yet answered); each
//   answer frees a slot and the next goes. A change made while the socket is
//   open goes at once, behind any older one still waiting, never ahead of it.
// - An ack takes the change out of the outbox. The list acknowledges a change
//   it already holds without applying it again, so sending one twice (a
//   socket that dropped before its ack) is safe.
// - A refusal with `retry` (too many changes this hour, a fault of ours)
//   keeps the change and holds the whole drain for a backoff of 30 s,
//   doubling to 10 min; then it starts again from the oldest.
// - A refusal without `retry` (a shape the list will never take, a kind only
//   the server writes) takes the change out of the outbox and into the
//   store's refused(), so it is not sent for ever; sync then asks for the
//   whole list, so this device's copy loses the edit the list did not take.
// - A refusal that names no change (an older list, or a message it could not
//   read) is not pinned on any change: nothing leaves the outbox for it.
// - A socket that closes leaves what was in flight in the outbox; the next
//   socket sends it again after its own hello.
import type { Change } from '@shared/edit';
import type { ServerMessage } from '@shared/socket';
import type { Store } from '../store/store';

/** Changes sent on one socket and not yet answered, at most. */
export const DRAIN_WINDOW = 50;
/** The hold after a refusal that will pass: 30 s, doubling, at most 10 min, jittered like the reconnect. */
export const RETRY_MS = { first: 30_000, max: 10 * 60_000 } as const;

/** The wait before attempt `n` (from 0): `first` doubling to `max`, each between half and all of that. */
export function jittered(attempt: number, bounds: { first: number; max: number }, random: () => number = Math.random): number {
  const ceiling = Math.min(bounds.max, bounds.first * 2 ** attempt);
  return Math.round(ceiling * (0.5 + random() * 0.5));
}

type Refusal = Extract<ServerMessage, { type: 'error' }>;

export interface Drain {
  /** A socket opened; `send` puts one change on it. Nothing goes until answered(). */
  open(send: (change: Change) => void): void;
  /** The list's answer to the socket's hello has been applied here: send what waits. */
  answered(): void;
  /** Sends what may go now (a change was made here, a slot came free). */
  pump(): void;
  /** The list holds this change. */
  ack(id: string): Promise<void>;
  /**
   * The list refused a message. 'refused': a change left the outbox for
   * good; 'held': the drain waits before it sends again; 'unknown': no
   * change was named, so nothing changed.
   */
  refused(error: Refusal): Promise<'refused' | 'held' | 'unknown'>;
  /** The socket closed: what was in flight goes again on the next one. */
  close(): void;
  /** For good: no more sends, no timer left behind. */
  stop(): void;
}

export interface DrainOptions {
  window?: number;
  random?: () => number;
}

export function createDrain(store: Store, opts: DrainOptions = {}): Drain {
  const window = opts.window ?? DRAIN_WINDOW;
  const random = opts.random ?? Math.random;
  let send: ((change: Change) => void) | undefined;
  let answered = false;
  /** Sent on this socket, not yet answered. */
  const inFlight = new Set<string>();
  /** Set while the drain waits out a refusal that will pass; it outlives a socket. */
  let hold: ReturnType<typeof setTimeout> | undefined;
  let holds = 0;
  let stopped = false;

  function pump(): void {
    if (stopped || !send || !answered || hold !== undefined) return;
    for (const change of store.pending()) {
      if (inFlight.size >= window) return;
      if (inFlight.has(change.id)) continue;
      inFlight.add(change.id);
      send(change);
    }
  }

  return {
    open(next) {
      send = next;
      answered = false;
      inFlight.clear();
    },
    answered() {
      answered = true;
      pump();
    },
    pump,
    async ack(id) {
      holds = 0; // the list took one, so whatever held us has passed
      // Out of the outbox before the slot frees, so a pump in between cannot send it again.
      await store.acknowledge(id);
      inFlight.delete(id);
      pump();
    },
    async refused({ id, retry, message }) {
      if (id === undefined) return 'unknown';
      if (retry) {
        inFlight.delete(id);
        hold ??= setTimeout(() => {
          hold = undefined;
          pump();
        }, jittered(holds++, RETRY_MS, random));
        return 'held';
      }
      await store.refuse(id, message);
      inFlight.delete(id);
      pump();
      return 'refused';
    },
    close() {
      send = undefined;
      answered = false;
      inFlight.clear();
    },
    stop() {
      stopped = true;
      send = undefined;
      inFlight.clear();
      clearTimeout(hold);
      hold = undefined;
    },
  };
}
